import React, { useState, useEffect, useMemo } from "react";
import { MobileAddClient } from "./mobileAddClient";
import axios from "axios";
import "./mobileLanding.css";
import { useNavigate } from "react-router-dom";
import Fuse from "fuse.js";
import api from "../../../api";

const COLUMNS = [
  { key: "Party",              label: "PARTY",       type: "text" },
  { key: "Bal Sale Potential", label: "BAL SALE",    type: "number" },
  { key: "Reconnect Date",     label: "RE-CONNECT",  type: "date" },
  { key: "Os Amt",             label: "AMT OVERDUE", type: "number" },
];

const sortRows = (rows, col, dir) => {
  if (!col) return rows;
  return [...rows].sort((a, b) => {
    const av = a[col] ?? (typeof a[col] === "number" ? 0 : "");
    const bv = b[col] ?? (typeof b[col] === "number" ? 0 : "");
    if (av === null || av === "") return 1;
    if (bv === null || bv === "") return -1;
    let cmp = 0;
    if (typeof av === "number" && typeof bv === "number") {
      cmp = av - bv;
    } else if (col === "Reconnect Date") {
      cmp = new Date(av) - new Date(bv);
    } else {
      cmp = String(av).localeCompare(String(bv));
    }
    return dir === "asc" ? cmp : -cmp;
  });
};

export const MobileLanding = () => {
  const navigate = useNavigate();
  const [apiResponse, setApiResponse] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [showAddClient, setShowAddClient] = useState(false);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [selectedCat, setSelectedCat] = useState("");
  const [showSortPanel, setShowSortPanel] = useState(false);

  const salesPersonId = localStorage.getItem("salesPersonId");

  const handleLogout = () => {
    localStorage.removeItem("salesPersonId");
    navigate("/mobile/login");
  };

  const handleLogoClick = () => {
    navigate("/mobile");
  };

  const fetchLandingPage = async (categoryId = "") => {
    try {
      const params = { SalespersonId: salesPersonId };
      if (categoryId) params.CategoryId = categoryId;
      const response = await api.get("/api/landingpage", { params });
      setApiResponse(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchLandingPage();
  }, []);

  const partySales = apiResponse?.data?.partySales || [];
  const categoryList = apiResponse?.data?.categoryList || [];

  const fuse = useMemo(
    () =>
      new Fuse(partySales, {
        keys: ["Party"],
        threshold: 0.2,
        ignoreLocation: true,
      }),
    [partySales]
  );

  const searched = searchText.trim()
    ? fuse.search(searchText).map((r) => r.item)
    : partySales;

  const sorted = useMemo(
    () => sortRows(searched, sortCol, sortDir),
    [searched, sortCol, sortDir]
  );

  const handleSort = (key, type) => {
    if (sortCol === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(key);
      setSortDir(type === "number" ? "desc" : "asc");
    }
    setShowSortPanel(false);
  };

  const sortIcon = (key) => {
    if (sortCol !== key) return "unfold_more";
    return sortDir === "asc" ? "arrow_upward" : "arrow_downward";
  };

  if (!apiResponse)
    return <div className="mobileLandingLoading">Loading...</div>;

  const summary = apiResponse.data.monthlySalesSummary || {};
  const recv = apiResponse.data.receivableSummary || {};
  const receipt = apiResponse.data.currentReceipt || {};

  const formatINR = (v) => {
    if (v === null || v === undefined) return "-";
    return `₹${Number(v).toLocaleString("en-IN")}`;
  };

  return (
    <div className="mobileLandingMain">
      {/* ── Header ── */}
      <div className="mobileLandingHeader">
        <div
          className="mobileLandingLogoBox"
          onClick={handleLogoClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") handleLogoClick();
          }}
          role="button"
          tabIndex={0}
          title="Go to mobile landing"
        >
          <span className="material-symbols-outlined">pie_chart</span>
        </div>
        <p className="mobileLandingAppName">Alpap CRM</p>
        <button className="mobileLandingLogoutBtn" onClick={handleLogout}>
          Logout
        </button>
      </div>

      {/* ── Scrollable body ── */}
      <div className="mobileLandingBody">
        {/* Search */}
        <div className="mobileLandingSearchWrapper">
          <span className="material-symbols-outlined mobileLandingSearchIcon">
            search
          </span>
          <input
            className="mobileLandingSearchInput"
            placeholder="Search clients, parties..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        {/* Add Client Button */}
        <button
          className="mobileLandingAddClientBtn"
          onClick={() => setShowAddClient(true)}
        >
          <span className="material-symbols-outlined">add</span>
          Add Client
        </button>

        {/* Current Month Sale Value card */}
        <div className="mobileLandingSaleCard">
          <p className="mobileLandingSaleCardTitle">Current month sale value</p>
          <div className="mobileLandingSaleRow">
            <span className="mobileLandingSaleLabel">Net Sale</span>
            <span className="mobileLandingSaleValue">{formatINR(summary.ItemValue)}</span>
          </div>
          <div className="mobileLandingSaleRow">
            <span className="mobileLandingSaleLabel">Gross Sale</span>
            <span className="mobileLandingSaleValue">{formatINR(summary.InvoiceAmt)}</span>
          </div>
        </div>

        {/* 2×2 stat cards */}
        <div className="mobileLandingStatGrid">
          <div className="mobileLandingStatCard">
            <p className="mobileLandingStatLabel">Total O/s</p>
            <p className="mobileLandingStatValueRed">{formatINR(recv["Total Receivable"])}</p>
          </div>
          <div className="mobileLandingStatCard">
            <p className="mobileLandingStatLabel">Over Due</p>
            <p className="mobileLandingStatValueRed">{formatINR(recv.Overdue)}</p>
          </div>
          <div className="mobileLandingStatCard">
            <p className="mobileLandingStatLabel">Qty</p>
            <p className="mobileLandingStatValueWhite">
              {summary.TotQty?.toLocaleString("en-IN") || "-"}
            </p>
            <p className="mobileLandingStatSub">Total quantity across all invoices</p>
          </div>
          <div className="mobileLandingStatCard">
            <p className="mobileLandingStatLabel">Current receipt</p>
            <p className="mobileLandingStatValueWhite">{formatINR(receipt["Current Receipt"])}</p>
            <p className="mobileLandingStatSub">Total receipts of salesperson</p>
          </div>
        </div>

        {/* Category Selection */}
        <div className="mobileLandingCategoryCard">
          <p className="mobileLandingCategoryTitle">Category Selection</p>
          <p className="mobileLandingCategorySub">
            On Selection show All details only for that Category
          </p>
          <div className="mobileLandingCategorySelectWrapper">
            <select
              className="mobileLandingCategorySelect"
              value={selectedCat}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedCat(value);
                fetchLandingPage(value);
              }}
            >
              <option value="">All Categories</option>
              {categoryList.map((cat) => (
                <option key={cat.CategoryId} value={cat.CategoryId}>
                  {cat.CategoryName}
                </option>
              ))}
            </select>
            <span className="material-symbols-outlined mobileLandingCategoryArrow">
              expand_more
            </span>
          </div>
        </div>

        {/* ── Clients card list ── */}
        <div className="mobileLandingClientsCard">
          {/* Header row */}
          <div className="mobileLandingClientsHeader">
            <p className="mobileLandingClientsTitle">Clients</p>
            <p className="mobileLandingClientsCount">{sorted.length}</p>
          </div>

          {/* Sort toggle button */}
          <div
            className="mobileLandingSortBtn"
            onClick={() => setShowSortPanel((v) => !v)}
          >
            <span className="material-symbols-outlined mobileLandingSortBtnIcon">
              tune
            </span>
            <span>Sort</span>
            {sortCol && (
              <span className="mobileLandingSortActive">
                {COLUMNS.find((c) => c.key === sortCol)?.label}
              </span>
            )}
            <span className="material-symbols-outlined mobileLandingSortChevron">
              {showSortPanel ? "expand_less" : "expand_more"}
            </span>
          </div>

          {/* Sort panel */}
          {showSortPanel && (
            <div className="mobileLandingSortPanel">
              {COLUMNS.map((col) => (
                <div
                  key={col.key}
                  className={`mobileLandingSortOption${
                    sortCol === col.key ? " mobileLandingSortOptionActive" : ""
                  }`}
                  onClick={() => handleSort(col.key, col.type)}
                >
                  <span>{col.label}</span>
                  <span className="material-symbols-outlined mobileLandingSortOptionIcon">
                    {sortIcon(col.key)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Client cards */}
          <div className="mobileLandingClientList">
            {sorted.map((item, index) => {
              const isOverdue = item["Os Amt"] > 0;
              return (
                <div
                  key={index}
                  className="mobileLandingClientRow"
                  onClick={() =>
                    navigate(`/mobile/details/${item.PartyId}`, {
                      state: {
                        partyName: item.Party,
                        partyPhone: item.ParPhone || null,
                        partyEmail: item.ParEmail || null,
                      },
                    })
                  }
                >
                  {/* Party name + overdue badge */}
                  <div className="mobileLandingClientRowTop">
                    <p className="mobileLandingClientName">{item.Party}</p>
                    {isOverdue && (
                      <span className="mobileLandingOverdueBadge">OVERDUE</span>
                    )}
                  </div>

                  {/* 3 data pills */}
                  <div className="mobileLandingClientRowBottom">
                    <div className="mobileLandingClientPill">
                      <span className="mobileLandingClientPillLabel">Bal Sale</span>
                      <span className="mobileLandingClientPillValue">
                        {item["Bal Sale Potential"]
                          ? `₹${item["Bal Sale Potential"].toLocaleString("en-IN")}`
                          : "-"}
                      </span>
                    </div>
                    <div className="mobileLandingClientPill">
                      <span className="mobileLandingClientPillLabel">Reconnect</span>
                      <span className="mobileLandingClientPillValue">
                        {item["Reconnect Date"]
                          ? new Date(item["Reconnect Date"]).toLocaleDateString("en-IN")
                          : "-"}
                      </span>
                    </div>
                    <div className="mobileLandingClientPill">
                      <span className="mobileLandingClientPillLabel">Overdue</span>
                      <span
                        className={`mobileLandingClientPillValue${
                          isOverdue ? " mobileLandingClientPillRed" : ""
                        }`}
                      >
                        {item["Os Amt"]
                          ? `₹${item["Os Amt"].toLocaleString("en-IN")}`
                          : "-"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {showAddClient && (
        <MobileAddClient onClose={() => setShowAddClient(false)} />
      )}
    </div>
  );
};